import axios, { AxiosInstance, AxiosResponse, CancelToken } from 'axios';
import { getApiConfig } from '../environment';
import { CreditRequestReasonEnum } from '../../../pages/orderly-for-distributors/Deliveries/enums/DeliveryEnums';

declare global {
  interface Window {
    Clerk?: {
      session?: {
        getToken?: ({ template }: { template: string }) => Promise<string | null>;
      };
    };
  }
}

const apiConfig = getApiConfig();

const axiosInstance: AxiosInstance = axios.create({
  baseURL: apiConfig.apiBaseUrl,
});

axiosInstance.interceptors.request.use(async (config) => {
   
  const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });
  if (!token) {
    throw new Error('Authorization token is missing.');
  }
  const currentUser = localStorage.getItem('user');
  const user = currentUser ? JSON.parse(currentUser) : null;

  config.headers['Accept'] = 'application/json';
  config.headers['Authorization'] = `Bearer ${token}`;
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  config.headers['Expires'] = '-1';
  config.headers['If-Modified-Since'] = 'Thu, 13 Feb 1985 13:40:01 GMT';
  config.headers['Cache-Control'] = 'no-cache';
  config.headers['Pragma'] = 'no-cache';
  config.headers['user-email'] = user.emailAddress || '';

  return config;
}, (error) => {
  return Promise.reject(error);
});

export interface Result<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface PaginatedList<T> {
  items: T[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  sort: string;
  direction: string;
}

export interface Order {
  id: number;
  statusId: number;
  status: string;
  emergencyOrder: boolean;
  submitted: boolean;
  orderTypeId: number;
  orderType: string;
  deliveryDate: string;
  cutOffDate: string;
  cutOffTime: string;
  totalCost: number;
  totalQuantity: number;
  supplierName: string;
  externallyCreatedOrder: boolean;
  deliveryDateUpdated: boolean;
  isAutoship: boolean;
  storeNumber: string;
  customerName: string;
}

export interface CurrencyConversionContent {
  ID: number;
  ToCurrency: string;
  ConversionRate: number;
  ConversionDate: string;
}

export interface ExchangeRateRequest {
  id: number;
  storeNumber: string;
  currency: string;
  rate: number | undefined;
  fromDate: string;
}

export enum OrderlySystem {
  OFS = 1,
  OFD = 2,
  OFC = 3
}

export interface OrderDetails {
  id: number;
  statusId: number;
  status: string;
  emergencyOrder: boolean;
  submitted: boolean;
  orderTypeId: number;
  orderType: string;
  deliveryDate: string;
  cutOffDate: string;
  cutOffTime: string;
  supplierNumber: string;
  supplierName: string;
  cutOffTimeLeft: CutOffTimeLeft;
  items: OrderItemDetails[];
  approvalStatus: string;
  rejectionReason: string;
  approvalComments: string;
  poNumber: string;
  approvalDate: string;
  documents: OrderDocument[];
  amendmentDetails: string;
  isAutoship: boolean;
  isParEnabled?: boolean;
}


export interface DeliveryOptions {
  name: string;
  shippingMethodId: string;
  charge: number;
}

export interface OrderDocument {
  documentName: string;
  documentType: string;
  documentContentId: number;
  webOrderId: number;
  documentId: number;
  fileName: string;
  fileType: string;
  fileDataBase64: string;
  fileData: Uint8Array;
  createdOn: string;
  lastUpdated: string;
  createdOnString: string;
  lastUpdatedString: string;
}

export interface ContainerFillResult {
    creditLimit: number;
    minFill: number;
    containerType: string;
    palletType: string;
    currentFill: number;
    orderWeight: number;
    maxPalletsPerContainer: number;
    totalPalletsNeeded: number;
    totalContainersUsed: number;
    containerFillPercents: number[];
    errors: string[];
}

export interface ShippingDetailsRequest {
  deliveryMethod: string;
  deliveryMethodId: string;
  orderStatus: string;
  deliveryCharge: number;
  totalDeliveryCharge?: number; 
  isDisabled?: boolean;
  country: string;
  location: string;
  containerType?: number;
  palletType?: number;
  deliveryOptions: DeliveryOptions[];
}

export interface CutOffTimeLeft {
  days: number;
  hours: number;
  minutes: number;
}

export interface OrderItemDetails {
  id: number;
  itemId: number;
  sku: string;
  name: string;
  primaryUom: string;
  orderableUom: string;
  price: number;
  gbpPrice: number;
  categoryId: number;
  packSize: string;
  category: string;
  stockOnHand: number;
  stockOnOrder: number;
  quantity: number;
  confirmedQuantity: number;
  shippedQuantity: number;
  imageId: number;
  thumbnailUrl: string;
  maxQuantity: number;
  minQuantity: number;
  gtin: string;
  amendmentDetails?: string;
  priceOverrideOriginalPrice?: number;
  priceOverrideReason?: string;
  calculatedParQuantity?: number | null;
  parQuantityApplied?: number | null;
  isQuantityOverridden?: boolean;
}

export interface OrderItem {
  id: number;
  sku: string;
  quantity: number;
  predictedQuantity?: number;
}

export interface OrderType {
  id: number;
  name: string;
}

export enum CategoryIconType {
  None = 0,
  Produce = 1,
  Dairy = 2,
  Bakery = 3,
  Beverages = 4,
  Meat = 5,
  Frozen = 6,
  Snacks = 7,
  Grocery = 8,
  Cleaning = 9,
  Packaging = 10,
}

export interface Category {
  id: number;
  name: string;
  display: boolean;
  showInFilters: boolean;
  showInNavigation: boolean;
  priority: number;
  fullCategoryString: string;
  parentId: number | null;
  parentName: string;
  parentFullCategoryString: string;
  iconType?: CategoryIconType;
}

export interface Item {
  id: number;
  sku: string;
  name: string;
  categoryId: number;
  category: string;
  stockOnHand: number;
  stockOnOrder: number;
  price: number;
  gbpPrice: number;
  unitPrice: number;
  casePrice: number;
  minimumUnitPrice: number;
  por: number;
  rrp: number;
  packSize: string;
  primaryUom: string;
  orderableUom: string;
  barcode: string;
  barcodeUom: string;
  imageId: number;
  thumbnailUrl: string;
  maxQuantity: number;
  minQuantity: number;
  quantityInUom: number;
  internalId: string;
  legacyProductCode: string;
  caseDimensions: string;
  orderCategory: string;
  productCategory: string;
  gtin: string;
  vatRate: number;
  weight: number;
  casesPerPallet: number;
  casesPerLayer: number;
  minShippingLife: number;
  minShelfLife: number;
  singlePick: boolean;
  caseSize: number;
  missingRequiredAttributes?: string[];
  missingRequiredDocuments?: string[];
  itemTags?: ItemTagSummary[];
  isFrozen: boolean;
  parLevel?: number;
}

export interface ItemTagSummary {
  id: number;
  name: string;
  textColor: string;
  backgroundColor: string;
  iconKey: string;
}
export interface CatalogueProducts {
  catalogueName: string;
  items: PaginatedList<Item>;
}

export interface ProductAttributesRequest {
  sku: string;
  attributes: { attributeDefinitionId: number; value: string }[];
}

export interface ProductAttributeModel {
  attributeDefinitionId: number;
  fieldName: string;
  value: string;
  dataType: string;
  mask?: string;
  isOptional: boolean;
  displayOrder: number;
  isMarketSpecific: boolean;
  isMultilineText: boolean;
}

export interface ItemDocument {
  Id: number;
  ProductId: number;
  DocumentId?: number;
  DocumentName: string;
  EffectiveStartDate?: string;
  EffectiveEndDate?: string;
  LastUpdated?: string;
  DocumentType: string;
}

export interface ItemForOrder {
  id: number;
  sku: string;
  name: string;
  categoryId: number;
  category: string;
  stockOnHand: number;
  stockOnOrder: number;
  price: number;
  primaryUom: string;
  orderableUom: string;
  barcode: string;
  barcodeUom: string;
  imageId: number;
  thumbnailUrl: string;
  calculatedParQuantity?: number | null;
  parQuantityApplied?: number | null;
  isQuantityOverridden?: boolean;
}

export interface AddedItem {
  item: ItemForOrder;
  quantity: number;
  isNew: boolean;
  maxQuantity: number;
  minQuantity: number;
}

export interface DeliverySchedule {
  id: number;
  customerId: number;
  orderType: number;
  cutOffDate: string;
  cutOffTime: string;
  shipDate: string;
  deliveryDate: string;
  supplier: string;
  distributionCenter: string;
}

export interface OrdersFilterOptions {
  storeNumber: string | undefined;
  currentStoreNumber?: string;
  orderNumber: string | undefined;
  item: string | undefined;
  orderType: number;
  status: number;
  deliveryDateFrom: string | undefined;
  deliveryDateTo: string | undefined;
  pageNumber: number;
  pageSize: number;
  sort: string | undefined;
  direction: string | undefined;
}

export interface DeliveryListFilterOptions {
  storeNumber: string | undefined;
  orderNumber: string | undefined;
  orderType: number;
  deliveryStatus: number;
  creditRequestStatus: number;
  deliveryDateFrom: string | undefined;
  deliveryDateTo: string | undefined;
  pageNumber: number;
  pageSize: number;
  sort: string | undefined;
  direction: string | undefined;
}

export interface ItemsFilterOptions {
  storeNumber: string | undefined;
  item: string | undefined;
  categoryId: number;
  orderType: number;
  supplier: string | undefined;
  pageNumber: number;
  pageSize: number;
  sort: string | undefined;
  direction: string | undefined;
  itemIds?: number[] | undefined;
}
export interface ForecastOrdersReportRequest {
  storeNumbers: string[],
  fromDate: string,
  toDate: string
}

export interface DeliveryReportRequest {
  days: number,
  storeNumbers: string[]
}

export interface ItemFilterParams {
  marketName: string | undefined;
  storeNumber: string | undefined;
  search: string | undefined;
  pageNumber: number;
  pageSize: number;
}

export interface CatalogueFilterOptions {
  storeNumber: string | undefined;
  name: string | undefined;
  pageNumber: number;
  pageSize: number;
  sort: string | undefined;
  direction: string | undefined;
}

export interface CatalogueProductsFilterOptions {
  storeNumber: string | undefined;
  searchTerm: string | undefined;
  category: string | undefined;
  pageNumber: number;
  pageSize: number;
  sort: string | undefined;
  direction: string | undefined;
}

export interface SimpleCategory {
  id: number;
  name: string;
}

export interface CreateOrderRequest {
  storeNumber: string;
  emergencyOrder: boolean;
  orderTypeId: number;
  deliveryDate: string;
  cutOffDate: string;
  supplierNumber: string;
  orderlySystem: OrderlySystem;
  items: OrderItem[];
}

export interface DeleteOrderItemsRequest {
  orderId: number;
  items: number[];
}

export interface AddOrderItemRequest {
  orderId: number;
  itemId: number;
  quantity: number;
  predictedQuantity?: number;
}

export interface OrderItemUpdate {
  id: number;
  quantity: number | null;
  predictedQuantity?: number;
  overridePrice?: number;
  overrideReason?: string;
}

export interface OrderUpdate {
  id: number;
  orderlySystem: OrderlySystem;
  items: OrderItemUpdate[];
}

export interface OrderForm {
  categories: OrderFormCategory[];
  availableDates: DeliveryDates[];
  isOrderTypeAutoship: boolean;
  isParEnabled?: boolean;
}

export interface DeliveryDates {
  deliveryDate: string;
  cutOffTime: string;
}

export interface OrderFormCategory extends Category {
  items: OrderFormItem[];
}

export interface OrderFormItem extends Item {
  quantity: Array<number | null>;
  lastTimeSold: string;
  parQuantityApplied?: Array<number | null>;
  calculatedParQuantity?: number | null;
}

export interface OrderFormUpdate {
  storeNumber: string;
  orderTypeId: number;
  supplierNumber: string;
  items: OrderFormItemUpdate[];
  dates: string[];
}

export interface OrderFormItemUpdate {
  id: number;
  sku: string;
  dates: OrderFormItemUpdateDate[];
}

export interface OrderFormItemUpdateDate {
  date: string;
  quantity: number | null;
  predictedQuantity?: number;
}

export interface FileResponse {
  fileData: string;
  fileName: string;
}

export interface FileRequest {
  fileData: string;
  fileName: string;
}

export interface Supplier {
  id: number;
  name: string;
  number: string;
  minimumOrderValue?: number;
}

export interface DsdSupplier extends Supplier {
  enabled: boolean;
}

export interface DeliveriesListModel {
  OrderId: string;
  StoreId: string;
  OrderType: string;
  ShippedQuantity: string;
  ValueShipped: string;
  DeliveryDate: string;
  DeliveryStatus: string;
  DeliveryReceivedDate: string;
  CreditRequestStatus: string;
  ClaimValue: number;
}

export interface News {
  id: number;
  title: string;
  teaser: string;
  body: string;
  imageId: number;
  thumbnailUrl: string;
  dataFromOM: boolean;
  startDate: string;
  imagesUrl: string[] | null | undefined;
  files: Files[] | null;
}

export interface Files {
  fileUrl: Item;
  fileDescription: number;
}

export interface OutOfStockUrgentAction {
  context: string;
}
export interface OrderFormFilterOptions {
    storeNumber: string | undefined;
    orderTypeId: number;
    categoryId: number;
    orderBy: string;
    direction: string;
    item: string;
    supplierNumber: string;
}

export interface DefaultOrderSettings {
  isStarbucksStore: boolean;
  isFreshAndDairyEnabled: boolean;
  isAmbientEnabled: boolean;
  isWarningEmailEnabled: boolean;
  dsdSuppliers: DsdSupplier[];
}

export interface Catalogue {
  id: number;
  name: string;
  orderType?: string;
  market?: string;
  retailers?: string[];
  retailerCount: number;
}

export interface Instance {
  id: number;
  name: string;
  userName: string;
  domainName: string;
  storeSslRoot: string;
  apiKey: string;
  configuration: InstanceConfiguration;
}

export interface InstanceConfiguration {
  configId?: number;
  isOrderlySaaS?: boolean;
  uomGroup: string;
  brand: string;
  production?: boolean;
  sendGridEmailTemplate: string;
  system: OrderlySystem;
}

export interface Market {
  customerTypeId: number;
  instanceName: string;
  companyId: number;
  name: string;
  timezone: string;
  timezoneOffset?: number;
  currencyCode: string;
  containerFillMethod?: number;
  tenantId?: number;
  systemIds?: number[];
  uuid?: string;
  isParEnabled?: boolean;
}

export interface MarketOrderType {
  companyOrderTypeId: number;
  instanceName: string;
  companyId: number;
  orderTypeId: number;
  name: string;
  marketName: string;
  customerTypeId?: number;
  internal: boolean;
  external: boolean;
  code: string;
  cutoff: MarketOrderTypeCutoff;
}

export interface MarketOrderTypeCutoff {
  customerTypeOrderTypeId?: number;
  orderCutOffTime?: string;
  canPushOrderInEnteredDOS?: boolean;
  supplierName: string;
  supplierEmailAddress: string;
  supplierEmailAddress2: string;
}

export interface MarketSupplier {
  supplierId: number;
  instanceName: string;
  companyId: number;
  marketName: string;
  customerTypeId: number;
  supplierNumber: string;
  supplierName: string;
  address1: string;
  address2: string;
  town: string;
  county: string;
  postcode: string;
  country: string;
  emailAddress: string;
  emailAddress2: string;
  fileFormat: string;
  publishMethod: string;
  supplierNumber2: string;
  cutOffTime: string;
  fileContents?: number;
  creditRequestCutOffTime: string;
  minimumOrderValue: number;
  timezone?: string;
  timezoneOffset?: number;
}

export interface MarketCatalogue {
  customerGroupId: number;
  instanceName: string;
  companyId: number;
  name: string;
  message: string;
  marketName: string;
  customerTypeId?: number;
  erpInternalId?: number;
  orderTypeIds: number[];
  supplierIds: number[];
}

export interface MarketStore {
  customerId: number;
  companyId: number;
  instanceName: string;
  customerTypeId: number;
  marketName: string;
  erpLocationId?: number;
  storeNumber: string;
  storeName: string;
  emailAddress: string;
  phoneNumber: string;
  shipToLocation: string;
  shipToAddress: string;
  town: string;
  county: string;
  country: string;
  postCode: string;
  inventoryEnabled?: boolean;
  inventoryTestEnabled?: boolean;
  inventoryStartDate?: string;
  suppliersAccountNumber?: IdNameValueModel[],
  orderTypesAccountNumber?: IdNameValueModel[],
  isParEnabled?: boolean;
  catalogueIds: number[];
  odsConfiguration: MarketStoreOdsConfiguration[];
}

export interface IdNameValueModel {
  id: number;
  name: string;
  value: string;
}

export interface MarketStoreOdsConfiguration {
  deliveryScheduleRuleId?: number;
  orderTypeId: number;
  orderType?: string | null;
  week: number;
  deliveryDay: number;
  daysToCutoff: string;
  supplierNumber?: string | null;
  supplierName?: string | null;
}

export interface MarketStoreUploadRequest {
  companyId: number;
  customerTypeId: number;
  fileBase64: string;
}

export interface MarketStoreUploadResult {
  created: number;
  updated: number;
  errors: string[];
}

export interface UomType {
  uomTypeId: number;
  companyId: number;
  instanceName: string;
  created?: string;
  updated?: string;
  uomName: string;
  isMeasurement?: boolean;
  isBase?: boolean;
  uomCode: string;
  inventoryUomCode: string;
  isSystemUom?: boolean;
}

export interface MarketCategory {
  categoryId: number;
  parentId: number;
  parentName: string;
  name: string;
  companyId: number;
  instanceName: string;
}

export interface MarketItem {
  contentId?: number;
  companyId: number;
  customerTypeId?: number;
  itemNumber: string;
  marketItemNumber: string;
  erpInternalId?: number;
  itemName: string;
  itemFriendlyName: string;
  marketItemName: string;
  primaryUom: string;
  orderableUom: string;
  primaryCategoryId?: number;
  marketPrimaryCategoryId?: number;
  primaryCategoryName?: string;
  marketPrimaryCategoryName?: string;
  packSize?: number;
  caseSize?: number;
  innerPackSize?: number;
  weight?: number;
  volume?: number;
  minQuantity?: number;
  minQuantityStartDate?: string;
  minQuantityEndDate?: string;
  maxQuantity?: number;
  maxQuantityStartDate?: string;
  maxQuantityEndDate?: string;
  startDate?: string;
  endDate?: string;
  productType?: string;
  parLevel?: number;
  catalogueIds: number[];
  catalogueEntries: MarketItemCatalogueEntry[];
  orderTypeIds: number[];
  orderTypeEntries: MarketItemOrderTypeEntry[];
  uoms: MarketItemUom[];
  imageUrl?: string;
  contentSuppliers?: ContentSupplier[];
}

export interface StoreLevelMOQFilterOptions {
  companyId: string;
  customerTypeId: string;
  itemNumber: string;
  searchTerm: string;
  sort: string;
  direction: string;
  pageNumber: number;
  pageSize: number;
}

export interface StoreLevelMOQ {
  id: number;
  companyId: number;
  customerNumber: string;
  storeName?: string;
  productCode: string;
  quantityType: string; // "1" = min, "2" = max
  quantity: number | null;
  startDate: string;
  endDate: string;
}

export interface ContentSupplier {
  id: number;
  contentId: number;
  supplierId: number;
  itemNumber: string;
  price: string;
}

export interface MarketItemCatalogueEntry {
  catalogueId: number;
  startDate?: string;
  endDate?: string;
}

export interface MarketItemOrderTypeEntry {
  orderTypeId: number;
  startDate?: string;
  endDate?: string;
}

export interface MarketItemUom {
  id: number;
  uomTypeId: number;
  uomTypeName: string;
  uomTypeCode: string;
  isOrderableUom: boolean;
  isPrimaryUom: boolean;
  isMeasurement: boolean;
  uomName: string;
  numberOfBaseUnitsOrEaches: number;
  weight?: number;
  volume?: number;
  price?: number;
}

export interface MarketItemUploadRequest {
  companyId: number;
  customerTypeId: number;
  fileBase64: string;
}

export interface MarketItemUploadResult {
  created: number;
  updated: number;
  errors: string[];
}

export interface InstanceFilterOptions {
  name?: string;
  sort?: string;
  direction?: string;
  pageNumber: number;
  pageSize: number;
}

export interface MarketFilterOptions {
  companyId?: string;
  name?: string;
  sort?: string;
  direction?: string;
  pageNumber: number;
  pageSize: number;
}

export interface MarketOrderTypeFilterOptions {
  companyId?: string;
  customerTypeId?: string;
  searchTerm?: string;
  sort?: string;
  direction?: string;
  pageNumber: number;
  pageSize: number;
}

export interface MarketSupplierFilterOptions {
  companyId?: string;
  customerTypeId?: string;
  searchTerm?: string;
  sort?: string;
  direction?: string;
  pageNumber: number;
  pageSize: number;
}

export interface MarketCatalogueFilterOptions {
  companyId?: string;
  customerTypeId?: string;
  searchTerm?: string;
  sort?: string;
  direction?: string;
  pageNumber: number;
  pageSize: number;
}

export interface MarketStoreFilterOptions {
  companyId?: string;
  customerTypeId?: string;
  searchTerm?: string;
  sort?: string;
  direction?: string;
  pageNumber: number;
  pageSize: number;
}

export interface UomTypeFilterOptions {
  companyId?: string;
  searchTerm?: string;
  sort?: string;
  direction?: string;
  pageNumber: number;
  pageSize: number;
}

export interface MarketCategoryFilterOptions {
  companyId?: string;
  searchTerm?: string;
  sort?: string;
  direction?: string;
  pageNumber: number;
  pageSize: number;
}

export interface MarketItemFilterOptions {
  companyId?: string;
  customerTypeId?: string;
  searchTerm?: string;
  sort?: string;
  direction?: string;
  pageNumber: number;
  pageSize: number;
}

export interface DeliveryMethod {
  ShippingMethodId: number;
  Name: string;
  Charge: number;
  CompanyId: number;
  ShippingModelId: number;
}

export interface DeliveryMethodListModel {
  ShippingMethodList: DeliveryMethod[];
}

export interface AutoshipTemplate {
  id?: number | null;
  name: string;
  orderTypeId: number;
  selectedReasons: number[];
  selectedItems: number[];
  selectAll: boolean;
}

export interface AutoshipTemplateModel {
  id: number | null;
  name: string;
  companyId: number;
  company: string;
  customerTypeId: number; 
  customerType: string;
  orderTypeId: number;
  orderType: string;
  selectedReasons: IdNameModel[];
  reasons: string;
  odsTypeId: number | null;
  status: number;
  createDate: string;
  itemCount?: number | null;
  scheduleCount?: number | null;
  selectedItems: number[];
  expandedItems: StoreItemModel[];
  hasPublishedSchedules: boolean;
  existingTemplates: string[];
  selectAll: boolean;
}

export interface AutoshipmentTemplatesFilterOptions {
  id?: string;
  name: string;
  orderTypeId: number;
  statusId: number;
  sort: string | undefined;
  direction: string | undefined;
  pageNumber: number;
  pageSize: number;
}

export interface AutoshipmentSchedulesFilterOptions {
  name: string;
  templateId: number;
  sort: string | undefined;
  direction: string | undefined;
  pageNumber: number;
  pageSize: number;
}

export interface StoreODS {
  id: number | null;
  autoshipScheduleId?: number | null;
  customerId: number;
  storeNumber: string;
  storeName?: string | null;
  deliveryDate?: Date | null | string;
}

export interface StoreAvailableODS {
  customerId: number;
  storeNumber: string;
  deliveryDates: string[];
}

export interface StoreItemModel {
  id: number | null;
  customerId: number;
  storeNumber?: string;
  storeName?: string;
  itemId: number;
  itemNumber?: string;
  itemDescription?: string;
  itemUom?: string;
  quantity: number | null;
  lotNumber: string;
}

export interface StoreItem {
  autoshipScheduleId?: number | null;
  customerId: number;
  itemId: number;
  quantity: number | null;
  lotNumber: string;
}

export interface AutoshipScheduleModel {
  id?: number;
  name: string;
  orderTypeId: number;
  orderType: string;
  templateId?: number | null;
  templateName: string;
  companyId: number;
  company: string;
  customerTypeId: number;
  customerType: string;
  statusId: number;
  createDate: string;
  odsTypeId?: number | null;
  publishDate?: string | null; 
  shipDate?: string | null; 
  deliveryDate?: string | null;
  itemCount?: number | null;
  scheduleCount?: number | null;
  stores: StoreODS[];
  items: StoreItemModel[];
}

export interface AutoshipSchedule {
  id?: number | string | null;
  name: string;
  orderTypeId: number;
  templateId?: number | null;
  companyId: number;
  customerTypeId: number;
  customerType: string;
  odsTypeId?: number | null;
  publishDate: Date | null | string;
  shipDate: Date | null | string;
  deliveryDate: Date | null | string;
  stores: StoreODS[];
  items: StoreItem[];
  isPublish: boolean;
  languageCode: string;
}

export interface IdNameModel {
  id: number;
  name: string;
}

export interface RetailerEdnOrderModel {
    FileName?: string | undefined;
    OrderId?: string | undefined;
    StoreId?: string | undefined;
    CustomerId?: string | undefined;
    MessageType?: string | undefined;
    StoreIdPrefix?: string | undefined;
    ToEmailAddress?: string | undefined;
    DeliveryNetwork?: string | undefined;
    OrderRaisedDate?: string | undefined;
    ShipToDeliverAt?: string | undefined;
    CustomerPONumber?: string | undefined;
    ShipToLocationId?: string | undefined;
    ShipToAddressCity?: string | undefined;
    ShipToAddressName?: string | undefined;
    OrderReferenceCode?: string | undefined;
    ShipFromLocationId?: string | undefined;
    ShipToAddressLine1?: string | undefined;
    ShipToAddressLine2?: string | undefined;
    ShipToAddressPostCode?: string | undefined;
    ShipToDeliverLatestAt?: string | undefined;
    ShipToAddressCountryCode?: string | undefined;
    Items?: EdnOrderItem[] | undefined;
}

export interface EdnOrderItem {
    ItemId?: string | undefined;
    ItemLineId?: number | undefined;
    ItemCaseSize?: number | undefined;
    QuantityType?: string | undefined;
    IsSubstitutePin?: boolean | undefined;
    ItemAlternateId?: ItemAlternateId | undefined;
    ItemDescription?: string | undefined;
    QuantityOrdered?: number | undefined;
    QuantityShipped?: number | undefined;
    PriceConfirmedAmount?: number | undefined;
    PriceConfirmedTaxRate?: number | undefined;
    PriceConfirmedCurrency?: string | undefined;
    IsSubstitutePinRequired?: boolean | undefined;
    PriceConfirmedEachesAmount?: number | undefined;
}

export interface ItemAlternateId {
    SkuMin?: string | undefined;
    SkuPin?: string | undefined;
    ClientId?: string | undefined;
    SkuLegacy?: string | undefined;
    BarcodeEan?: string | undefined;
}

export interface DeliveryScheduleFilterOptions {
  instanceId: number;
  marketId: number;
  deliveryDateFrom: string;
  deliveryDateTo: string;
  cutOffDateFrom: string;
  cutOffDateTo: string;
  orderTypes: string;
  supplierNumber: string;
  stores: string;
  sort: string | undefined;
  direction: string | undefined;
  pageNumber: number;
  pageSize: number;
}

export interface DeliveryScheduleModel {
  id: number;
  customerId: number;
  storeNumber: string;
  storeName: string;
  orderTypeId: number;
  orderType: string;
  cutOffDate: string;
  deliveryDate: string;
  cutOffTime: string;
  supplierNumber: string;
  supplierName: string;
  rowNumber?: number | null;
}

export interface ODSImportResultModel {
  updated: DeliveryScheduleModel[];
  created: DeliveryScheduleModel[];
  deleted: DeliveryScheduleModel[];
  invalid: DeliveryScheduleModel[];
}

export interface BulkODSDateUpdateModel {
  ids: number[];
  date: string;
  isCutOffDate: boolean;
  companyId: number;
  languageCode: string;
}

export interface AddODSModel {
  instanceId: number;
  marketId: number;
  orderTypeId: number;
  supplier: string | null;
  cutOffDate: string;
  deliveryDate: string;
  storeNumbers: string[];
  languageCode: string;
}

export interface DeliveryOrderItemModel {
    ItemId: number;
    Sku: string;
    Name: string;
    OrderedQuantity: number;
    OrderedUom: string;
    ShippedQuantity: number;
    ReceivedQuantity: number;
    ReceivedUom: string;
    OrderReceived: boolean;
    Notes?: string;
    CreditRequestItemID?: number;
    ConvertedUomCreditValue?: number;
    ConvertedUomCreditQuantity?: number;
    HasPassedClaimPeriod: boolean;
    Barcode: string;
}

export interface DeliveryOrderItemUpdateModel {
    ItemId: number;
    OrderId: number;
    ReceivedQuantity: number;
    ReceivedUom: string;
    Notes?: string;
}

export interface BulkOrderItemModifier {
  id: number;
  name: string;
  modifierType: 'percentageUplift' | 'quantityOffset';
  modifierValue: number;
}

export interface BulkOrderItem {
  storeNumber: string;
  storeName: string;
  itemSku: string;
  itemName: string;
  category: string;
  price: number;
  orderableUom: string;
  primaryUom: string;
  packSize?: string;
  stockOnHand?: string;
  /** Calculated PAR quantity after SOH and min/max rules are applied. */
  par?: number;
  forecast?: number;
  demandModifiers: BulkOrderItemModifier[];
  /** Aggregated modifier name string – used in the Excel download export. Not displayed in the UI. */
  demandModifierName?: string;
  baseOrderQuantity?: number | null;
  /** Final calculated order quantity after override/PAR fallback and demand modifiers. Read-only in the UI. */
  orderQuantity: number;
  itemId: number;
  minQuantity?: number;
  maxQuantity?: number;
  orderId?: number;
  /** Whether the linked order is an emergency (single-push outside ODS) order. Always false when no order is linked. */
  isEmergencyOrder: boolean;
}

export interface BulkOrdersRequest {
  customerTypeId: number;
  orderTypeId: number;
  deliveryDate: string;
  supplierNumber?: string;
  categoryId?: number;
  itemSearch?: string;
}

export interface BulkOrderODSAvailability {
  date: string;
  availableStores: number;
  totalStores: number;
  coveragePercentage: number;
}

export interface CreditRequestItemModel {
    WebOrderItemCreditRequestID: number;
    WebOrderItemID: number;
    WebOrderID: number;
    OrderCreditRequestStatus: string;
    ClaimReason?: number;
    ClaimNotes?: string;
    CreditNotes?: string;
    ClaimQuantity?: number;
    ClaimValue?: number;
    FileNames: string[];
    OrderType: string;
    ConfirmedCreditQuantity?: number;
    ConfirmedCreditUom?: string;
    ConfirmedCreditValue?: number;
    Notes?: string;
    ItemClaimStatus: number;
    ItemId: number;
    Sku: string;
    Name: string;
    ShippedQuantity: number;
    ReceivedQuantity: number;
    ShippedUom: string;
    ReceivedUom: string;
    ReviewedOn?: string;
    HasPassedClaimPeriod: boolean;
}

export interface CreditRequestFile {
    FileName: string;
    FileType: string;
    FileData: string;
}

export interface SaveCreditRequestItemModel {
    WebOrderItemCreditRequestID: number;
    Reason: CreditRequestReasonEnum;
    Notes: string;
    Files?: CreditRequestFile[];
}

export interface CreditValueRequest {
    ItemId: number;
    OrderNumber: string;
    CreditQuantity: number;
    CreditUom: string;
}

export interface CreditValueResponse {
    CreditValue: number;
}

export interface CreditReviewLineModel {
    WebOrderItemCreditRequestID: number;
    ItemId: number;
    CreditQuantity: number;
    CreditUom: string;
    CreditNote: string;
    Status?: string;
}

export interface FinanceTableRowModel {
    id: number;
    date?: string;
    reference?: string;
    totalItems?: string;
    goodsExVatOther?: string;
    vatOther?: string;
    goodsExVat20?: string;
    vat20?: string;
    goods0?: string;
    totalGoods?: string;
    totalVat?: string;
    total?: string;
    invoiceDate?: string;
    invoiceNo?: string;
    invoiceTotal?: string;
    statementNumber?: string;
    totalInvoicesThisWeek?: string;
}

export interface IDocumentBlob {
    FileName: string;
    Data: string;
    MimeType: string;
}


export interface ODSAvailability {
  date: string;
  availableStores: number;
  totalStores: number;
}

export interface OrderSubmitItem {
  storeNumber: string;
  orderId: number;
}

export interface BulkOrderSubmitRequest {
  orders: OrderSubmitItem[];
}

export interface OrderSubmitResult {
  storeNumber: string;
  orderId: number;
  success: boolean;
  message: string;
  alreadySubmitted: boolean;
}

export interface BulkOrderSubmitResponse {
  successCount: number;
  failureCount: number;
  results: OrderSubmitResult[];
}

export interface DemandModifier {
  id?: number;
  name: string;
  startDate: string;
  endDate: string;
  targetType: 'allItems' | 'specificCategories' | 'specificSKUs';
  targetCategories: number[];
  targetSKUs: string[];
  locationType: 'allFranchises' | 'specificFranchises' | 'specificStores';
  targetFranchises: number[];
  targetStores: string[];
  modifierType: 'percentageUplift' | 'quantityOffset';
  upliftPercentage: number;
  quantityOffset?: number;
  isActive: boolean;
}

export interface UpsertDemandModifierRequest {
  id?: number;
  name: string;
  startDate: string;
  endDate: string;
  modifierType: 'percentageUplift' | 'quantityOffset';
  modifierValue: number;
  itemTargetType?: 'allItems' | 'specificCategories' | 'specificSKUs';
  targetCategories?: number[];
  targetSKUs?: string[];
  locationTargetType?: 'allFranchises' | 'specificFranchises' | 'specificStores';
  targetFranchises?: number[];
  targetStores?: string[];
  isActive?: boolean;
}

export interface CompanyConfig {
  useInventorySKU?: boolean;
}

class Client {
  private static instance: Client;
  private axiosInstance: AxiosInstance;

  private constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  public static getInstance(): Client {
    if (!Client.instance) {
      Client.instance = new Client(axiosInstance);
    }
    return Client.instance;
  }

  public async getCompanyConfig(companyId: number): Promise<Result<CompanyConfig>> {
    try {
      const response: AxiosResponse<Result<CompanyConfig>> = await this.axiosInstance.get(`CompanyConfig/${companyId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getOrders(options: OrdersFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Order>>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: options.storeNumber ?? '',
        OrderNumber: options.orderNumber ?? '',
        Item: options.item ?? '',
        OrderType: options.orderType.toString() ?? '',
        Status: options.status.toString() ?? '',
        DeliveryDateFrom: options.deliveryDateFrom ?? '',
        DeliveryDateTo: options.deliveryDateTo ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': options.currentStoreNumber != null && options.currentStoreNumber !== '' ? options.currentStoreNumber : options.storeNumber };
      const response: AxiosResponse<Result<PaginatedList<Order>>> = await this.axiosInstance.get(`orders?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getDeliveries(options: DeliveryListFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<DeliveriesListModel>>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: options.storeNumber ?? '',
        OrderNumber: options.orderNumber ?? '',
        OrderType: options.orderType.toString() ?? '',
        DeliveryStatus: options.deliveryStatus.toString() ?? '',  
        CreditRequestStatus: options.creditRequestStatus.toString() ?? '',
        DeliveryDateFrom: options.deliveryDateFrom ?? '',
        DeliveryDateTo: options.deliveryDateTo ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': options.storeNumber };
      const response: AxiosResponse<Result<PaginatedList<DeliveriesListModel>>> = await this.axiosInstance.get(`deliveries?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getOrderAudit(storeNumber: string | undefined, orderNumber: number, cancelToken?: CancelToken): Promise<Result<(string | undefined | null)[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<(string | undefined | null)[]>> = await this.axiosInstance.get(`orders/audit/${orderNumber}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }
  
  public async getOnboardingDeliveryMethods(storeNumber?: string, cancelToken?: CancelToken): Promise<Result<DeliveryMethodListModel>> {
    try {
      const headers: Record<string, string> = {};
      if (storeNumber) {
        headers['ews-storeNumber'] = storeNumber;
      }
    
      const response: AxiosResponse<Result<DeliveryMethodListModel>> = await this.axiosInstance.get('GetDeliveryMethods', { 
        headers,
        cancelToken, 
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  //Finance Area
  public async getFinanceTableRows(storeNumber: string, type: string, filter?: string, pageNumber?: number, cancelToken?: CancelToken): Promise<Result<PaginatedList<FinanceTableRowModel>>> {
    try {
      const params = new URLSearchParams({
        type: type ?? '',
        filter: filter ?? '',
        pageNumber: pageNumber?.toString() ?? '1',
        pageSize: '10',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<PaginatedList<FinanceTableRowModel>>> = await this.axiosInstance.get(`orders/financial/getFinanceTableRows?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async downloadFinancialBlob(id: number, type: string, storeNumber: string, cancelToken?: CancelToken): Promise<Result<IDocumentBlob>> {
    try {
      const params = new URLSearchParams({
        id: id.toString(),
        type: type ?? '',
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber };

      const response: AxiosResponse<Result<IDocumentBlob>> = await this.axiosInstance.post(
        `orders/financial/blob?${params}`,
        null,
        { headers, cancelToken },
      );

      return response.data;

    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getOrderTypes(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OrderType[]>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: storeNumber ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<OrderType[]>> = await this.axiosInstance.get(`ordertypes?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getDeliverySchedules(storeNumber: string | undefined, orderType: number | undefined, supplierNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<DeliverySchedule[]>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: storeNumber ?? '',
        OrderType: orderType?.toString() ?? '',
        SupplierNumber: supplierNumber ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<DeliverySchedule[]>> = await this.axiosInstance.get(`deliveryschedules?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getItems(options: ItemsFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Item>>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: options.storeNumber ?? '',
        Item: options.item ?? '',
        OrderType: options.orderType.toString() ?? '',
        Category: options.categoryId?.toString() ?? '',
        Supplier: options.supplier?.toString() ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        ItemIds: options?.itemIds ? options.itemIds.join(',') : '',
      }).toString();
      const headers = { 'ews-storeNumber': options.storeNumber };
      const response: AxiosResponse<Result<PaginatedList<Item>>> = await this.axiosInstance.get(`products?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getItemsByFranchiseName(options: ItemFilterParams, cancelToken?: CancelToken): Promise<Result<PaginatedList<Item>>> {
    try {
      const params = new URLSearchParams({
        MarketName: options.marketName ?? '',
        StoreNumber: options.storeNumber ?? '',
        Search: options.search ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();
      const headers = { 'ews-storeNumber': options.storeNumber };
      const response: AxiosResponse<Result<PaginatedList<Item>>> = await this.axiosInstance.get(`products/getbymarketname?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }
  public async getCatalogues(options: CatalogueFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Catalogue>>> {
    try {
      const params = new URLSearchParams({
        Name: options.name ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': options.storeNumber };
      const response: AxiosResponse<Result<PaginatedList<Catalogue>>> = await this.axiosInstance.get(`catalogues?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getCatalogueRetailers(catalogueId: string, storeNumber: string, cancelToken?: CancelToken): Promise<Result<string[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<string[]>> = await this.axiosInstance.get(`catalogues/${catalogueId}/retailers`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getCatalogueProducts(catalogueId: string, options: CatalogueProductsFilterOptions, cancelToken?: CancelToken): Promise<Result<CatalogueProducts>> {
    try {
      const params = new URLSearchParams({
        SearchTerm: options.searchTerm ?? '',
        Category: options.category ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': options.storeNumber };
      const response: AxiosResponse<Result<CatalogueProducts>> = await this.axiosInstance.get(`catalogueProducts/${catalogueId}?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getCategoriesByCatalogue(storeNumber: string, catalogueId: string, cancelToken?: CancelToken): Promise<Result<Category[]>> {
    try {
      const params = new URLSearchParams({ CatalogueId: catalogueId }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<Category[]>> = await this.axiosInstance.get(`categoriesbycatalogue?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getCategories(storeNumber: string | undefined, orderType: number, supplier: string | undefined, cancelToken?: CancelToken): Promise<Result<Category[]>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: storeNumber ?? '',
        OrderType: orderType.toString(),
        Supplier: supplier?.toString() ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<Category[]>> = await this.axiosInstance.get(`categories?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getExchangeRates(storeNumber: string | undefined, pageNumber: number, pageSize: number, active: boolean, cancelToken?: CancelToken): Promise<Result<PaginatedList<CurrencyConversionContent>>> {
    try {
      const params = new URLSearchParams({
        PageNumber: pageNumber?.toString() ?? '',
        PageSize: pageSize?.toString() ?? '',
        Active: active ? 'true' : 'false',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<PaginatedList<CurrencyConversionContent>>> = await this.axiosInstance.get(`currency?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateExchangeRate(storeNumber: string | undefined, request: ExchangeRateRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('currency', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async deleteExchangeRate(
    storeNumber: string | undefined,
    id: number,
    cancelToken?: CancelToken,
  ): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(
        `currency?id=${id}`,
        { headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createOrder(request: CreateOrderRequest, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const headers = { 'ews-storeNumber': request.storeNumber };
      if (request.orderlySystem === undefined) {
        request.orderlySystem = OrderlySystem.OFS;
      }
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post<Result<number>>('orders', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getOrder(storeNumber: string | undefined, orderId: number, cancelToken?: CancelToken): Promise<Result<OrderDetails>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<OrderDetails>> = await this.axiosInstance.get(`orders/${orderId}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async deleteOrderItem(storeNumber: string | undefined, orderId: string, orderItemId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const url = `orders/${orderId}/OrderItems/${orderItemId}`;
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(url, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async addOrderItem(storeNumber: string | undefined, request: AddOrderItemRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post<Result<boolean>>('orders/orderitems/add', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async deleteOrderItems(storeNumber: string | undefined, request: DeleteOrderItemsRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post<Result<boolean>>('orders/deleteorderitems', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async deleteAllOrderItems(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post<Result<boolean>>(`orders/deleteallorderitems/${orderId}`, {}, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async checkoutOrder(storeNumber: string | undefined, orderId: string | undefined, notificationEmails?: string[], cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post<Result<boolean>>(`orders/checkoutorder/${orderId}`, { NotificationEmails: notificationEmails ?? [] }, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async submitOrder(storeNumber: string | undefined, orderId: string, orderDetails: OrderDetails, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/submit/${orderId}`, orderDetails, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async submitScheduledOrderAsEmergencyOrder(storeNumber: string | undefined, orderId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`Orders/SubmitScheduledOrderAsEmergencyOrder/${orderId}`, null, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async submitBulkOrders(request: BulkOrderSubmitRequest, cancelToken?: CancelToken): Promise<Result<BulkOrderSubmitResponse>> {
    try {
      const response: AxiosResponse<Result<BulkOrderSubmitResponse>> = await this.axiosInstance.post('orders/submitBulk', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { 
          success: false, 
          message: error.message, 
          data: { successCount: 0, failureCount: 0, results: [] }, 
        };
      } else {
        console.error('Unexpected error:', error);
        return { 
          success: false, 
          message: 'An unexpected error occurred.', 
          data: { successCount: 0, failureCount: 0, results: [] }, 
        };
      }
    }
  }

  public async getItemByBarcode(storeNumber: string | undefined, barcode: string, stockCount: string | undefined, user: string | undefined, cancelToken?: CancelToken): Promise<Result<Item>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber, 'stock-count': stockCount, 'user': user };
      const response: AxiosResponse<Result<Item>> = await this.axiosInstance.get(`products/barcode/${barcode}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getItemBySku(storeNumber: string | undefined, sku: string, forOfd?: boolean, cancelToken?: CancelToken): Promise<Result<Item>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<Item>> = await this.axiosInstance.get(`products/sku/${sku}?ForOfd=${forOfd ?? false}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getItemDocuments(storeNumber: string | undefined, sku: string, cancelToken?: CancelToken): Promise<Result<ItemDocument[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<ItemDocument[]>> = await this.axiosInstance.get(`products/documents/${sku}`, { headers, cancelToken });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getOrderDocuments(storeNumber: string | undefined, orderId: number, cancelToken?: CancelToken): Promise<Result<{ [itemId: number]: ItemDocument[] }>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<{ Key: number; Value: ItemDocument[] }[]>> = await this.axiosInstance.get(`orders/${orderId}/documents`, { headers, cancelToken });
      if (response.data.success && response.data.data) {
        const transformedData: { [itemId: number]: ItemDocument[] } = {};
        response.data.data.forEach(item => {
          transformedData[item.Key] = item.Value;
        });   
        return {
          success: response.data.success,
          message: response.data.message,
          data: transformedData,
        };
      }
      return {
        success: response.data.success || false,
        message: response.data.message || '',
        data: {},
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateOrder(storeNumber: string | undefined, request: OrderUpdate, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      if (request.orderlySystem === undefined) {
        request.orderlySystem = OrderlySystem.OFS;
      }
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('orders/update', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async getOrderByDeliveryDate(storeNumber: string | undefined, orderTypeId: number, deliveryDate: string, supplierNumber: string, cancelToken?: CancelToken): Promise<Result<OrderDetails>> {
    try {
      const params = new URLSearchParams({
        SupplierNumber: supplierNumber,
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<OrderDetails>> = await this.axiosInstance.get(`orders/ordertype/${orderTypeId.toString()}/deliverydate/${deliveryDate}?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getItem(storeNumber: string | undefined, itemId: number, cancelToken?: CancelToken): Promise<Result<Item>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<Item>> = await this.axiosInstance.get(`products/${itemId}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async generateForecastOrdersReport(storeNumber: string, request: ForecastOrdersReportRequest, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.post<Result<FileResponse>>('Orders/GenerateForecastOrdersReport', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async generateDeliveryReport(storeNumber: string, request: DeliveryReportRequest, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.post<Result<FileResponse>>('Orders/GenerateDeliveryReport', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async copyOrder(storeNumber: string | undefined, orderId: string | undefined, copyOrderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/${orderId}/copy/${copyOrderId}`, null, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async deleteOrder(storeNumber: string | undefined, orderId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(`orders/${orderId}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async getOrderForm(storeNumber: string | undefined, orderTypeId: number, categoryId: number, sort: string, direction: string, item: string, supplierNumber: string, cancelToken?: CancelToken): Promise<Result<OrderForm>> {
    try {
      const params = new URLSearchParams({
        OrderType: orderTypeId.toString() ?? '',
        Category: categoryId?.toString() ?? '',
        Sort: sort ?? '',
        Direction: direction ?? '',
        Item: item ?? '',
        SupplierNumber: supplierNumber ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<OrderForm>> = await this.axiosInstance.get(`orders/orderform?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async saveOrderForm(storeNumber: string | undefined, request: OrderFormUpdate, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('orders/orderform', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async downloadOrderForm(storeNumber: string | undefined, orderTypeId: number, supplierNumber: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const params = new URLSearchParams({ OrderType: orderTypeId.toString(), Supplier: supplierNumber }).toString();
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.get<Result<FileResponse>>(`orders/orderform/download?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async uploadOrderForm(storeNumber: string | undefined, orderTypeId: number, supplierNumber: string, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const params = new URLSearchParams({ Supplier: supplierNumber }).toString();
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/orderform/upload/${orderTypeId}?${params}`, request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async downloadOrderSpreadsheet(storeNumber: string | undefined, orderId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.get<Result<FileResponse>>(`orders/${orderId}/download`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async uploadOrderSpreadsheet(storeNumber: string | undefined, orderId: string, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/${orderId}/upload`, request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async downloadCatalogueExportAttributes(storeNumber: string | undefined, catalogueId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.get<Result<FileResponse>>(`catalogues/${catalogueId}/exportattributes/download`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async uploadCatalogueExportAttributes(storeNumber: string | undefined, catalogueId: string, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`catalogues/${catalogueId}/exportattributes/upload`, request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async getSuppliers(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<Supplier[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<Supplier[]>> = await this.axiosInstance.get('suppliers', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async GetNews(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<News[]>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: storeNumber ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<News[]>> = await this.axiosInstance.get(`news?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async GetOutOfStockAlert(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OutOfStockUrgentAction>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<OutOfStockUrgentAction>> = await this.axiosInstance.get('outOfStockAlert', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async GetUrgentAlerts(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OutOfStockUrgentAction>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<OutOfStockUrgentAction>> = await this.axiosInstance.get('urgentAlert', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getDefaultOrderSettings(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<DefaultOrderSettings>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<DefaultOrderSettings>> = await this.axiosInstance.get('customers/getdefaultordersettings', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateDefaultOrderSettings(storeNumber: string | undefined, request: DefaultOrderSettings, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('customers/updatedefaultordersettings', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async duplicateOrder(storeNumber: string | undefined, sourceOrderId: number, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post(`orders/duplicate/${sourceOrderId}`, null, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getStoreContainerData(storeNumber: string | undefined, orderNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<ContainerFillResult>> {
    try {
      const params = new URLSearchParams({
        storeNumber: storeNumber ?? '',
        orderId: orderNumber ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<ContainerFillResult>> = await this.axiosInstance.get(`orders/storeContainer?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async setShippingDetails(storeNumber: string, orderId: string, shippingDetails: ShippingDetailsRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const params = new URLSearchParams({
        storeNumber: storeNumber ?? '',
        orderId: orderId ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(
        `orders/setShippingDetails?${params}`,
        shippingDetails,
        { headers, cancelToken },
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getShippingDetails(
    storeNumber: string, 
    orderId: string, 
    shippingMethodId?: string, 
    cancelToken?: CancelToken,
  ): Promise<Result<ShippingDetailsRequest>> {
    try {
      const params = new URLSearchParams({
        storeNumber: storeNumber ?? '',
        orderId: orderId ?? '',
        shippingMethodId: shippingMethodId ?? '',
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<ShippingDetailsRequest>> = await this.axiosInstance.get(
        `orders/getShippingDetails?${params}`,
        { headers, cancelToken },
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else 
      {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getItemAttributes(storeNumber: string | undefined, sku: string, cancelToken?: CancelToken): Promise<Result<ProductAttributeModel[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<ProductAttributeModel[]>> = await this.axiosInstance.get(`itemattributes/${sku}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateItemAttributes(storeNumber: string | undefined, request: ProductAttributesRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('items/attributes', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async approveOrder(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/approve/${orderId}`, null, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async approveAmendment(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/approve-amendment/${orderId}`, null, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async rejectOrder(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/reject/${orderId}`, null, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async rejectAmendment(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/reject-amendment/${orderId}`, null, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async updateOrderDeliveryDate(storeNumber: string | undefined, orderId: string, deliveryDate: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(
        `orders/${orderId}/deliverydate`,
        { deliveryDate },
        { headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async getInstance(instanceId: string, cancelToken?: CancelToken): Promise<Result<Instance>> {
    try {
      const response: AxiosResponse<Result<Instance>> = await this.axiosInstance.get(`instances/${instanceId}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getInstances(options: InstanceFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Instance>>> {
    try {
      const params = new URLSearchParams({
        Name: options.name ?? '',
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();
      const response: AxiosResponse<Result<PaginatedList<Instance>>> = await this.axiosInstance.get(`instances?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createInstance(request: Instance, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('instances', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateInstance(instanceId: string, request: Instance, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`instances/${instanceId}`, request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createMarket(request: Market, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('markets', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateMarket(marketId: string, request: Market, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`markets/${marketId}`, request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarkets(options: MarketFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Market>>> {
    try {
      const params = new URLSearchParams({
        CompanyId: options.companyId ?? '',
        Name: options.name ?? '',
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();
      const response: AxiosResponse<Result<PaginatedList<Market>>> = await this.axiosInstance.get(`markets?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getCompanyIdByStoreNumber(storeNumber: string, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.get('markets/companyId', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarket(marketId: string, cancelToken?: CancelToken): Promise<Result<Market>> {
    try {
      const response: AxiosResponse<Result<Market>> = await this.axiosInstance.get(`markets/${marketId}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createMarketOrderType(request: MarketOrderType, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketordertypes', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateMarketOrderType(marketOrderTypeId: string, request: MarketOrderType, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketordertypes/${marketOrderTypeId}`, request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketOrderTypes(options: MarketOrderTypeFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketOrderType>>> {
    try {
      const params = new URLSearchParams({
        CompanyId: options.companyId ?? '',
        CustomerTypeId: options.customerTypeId ?? '',
        SearchTerm: options.searchTerm ?? '',
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();
      const response: AxiosResponse<Result<PaginatedList<MarketOrderType>>> = await this.axiosInstance.get(`marketordertypes?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketOrderType(marketOrderTypeId: string, cancelToken?: CancelToken): Promise<Result<MarketOrderType>> {
    try {
      const response: AxiosResponse<Result<MarketOrderType>> = await this.axiosInstance.get(`marketordertypes/${marketOrderTypeId}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createMarketSupplier(request: MarketSupplier, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketsuppliers', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateMarketSupplier(supplierId: string, request: MarketSupplier, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketsuppliers/${supplierId}`, request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketSuppliers(options: MarketSupplierFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketSupplier>>> {
    try {
      const params = new URLSearchParams({
        CompanyId: options.companyId ?? '',
        CustomerTypeId: options.customerTypeId ?? '',
        SearchTerm: options.searchTerm ?? '',
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();
      const response: AxiosResponse<Result<PaginatedList<MarketSupplier>>> = await this.axiosInstance.get(`marketsuppliers?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketSupplier(supplierId: string, cancelToken?: CancelToken): Promise<Result<MarketSupplier>> {
    try {
      const response: AxiosResponse<Result<MarketSupplier>> = await this.axiosInstance.get(`marketsuppliers/${supplierId}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createMarketCatalogue(request: MarketCatalogue, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketcatalogues', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateMarketCatalogue(catalogueId: string, request: MarketCatalogue, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketcatalogues/${catalogueId}`, request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketCatalogues(options: MarketCatalogueFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketCatalogue>>> {
    try {
      const params = new URLSearchParams({
        CompanyId: options.companyId ?? '',
        CustomerTypeId: options.customerTypeId ?? '',
        SearchTerm: options.searchTerm ?? '',
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();
      const response: AxiosResponse<Result<PaginatedList<MarketCatalogue>>> = await this.axiosInstance.get(`marketcatalogues?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketCatalogue(catalogueId: string, cancelToken?: CancelToken): Promise<Result<MarketCatalogue>> {
    try {
      const response: AxiosResponse<Result<MarketCatalogue>> = await this.axiosInstance.get(`marketcatalogues/${catalogueId}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createMarketStore(request: MarketStore, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketstores', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateMarketStore(storeId: string, request: MarketStore, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketstores/${storeId}`, request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketStore(storeId: string, cancelToken?: CancelToken): Promise<Result<MarketStore>> {
    try {
      const response: AxiosResponse<Result<MarketStore>> = await this.axiosInstance.get(`marketstores/${storeId}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketStores(options: MarketStoreFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketStore>>> {
    try {
      const params = new URLSearchParams({
        CompanyId: options.companyId ?? '',
        CustomerTypeId: options.customerTypeId ?? '',
        SearchTerm: options.searchTerm ?? '',
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();
      const response: AxiosResponse<Result<PaginatedList<MarketStore>>> = await this.axiosInstance.get(`marketstores?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async downloadMarketStores(companyId: string, customerTypeId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const params = new URLSearchParams({
        CompanyId: companyId,
        CustomerTypeId: customerTypeId,
      }).toString();
      const response: AxiosResponse<Result<FileResponse>> = await this.axiosInstance.get(`marketstores/download?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async uploadMarketStores(request: MarketStoreUploadRequest, cancelToken?: CancelToken): Promise<Result<MarketStoreUploadResult>> {
    try {
      const response: AxiosResponse<Result<MarketStoreUploadResult>> = await this.axiosInstance.post('marketstores/upload', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getUomTypes(options: UomTypeFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<UomType>>> {
    try {
      const params = new URLSearchParams({
        CompanyId: options.companyId ?? '',
        SearchTerm: options.searchTerm ?? '',
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();
      const response: AxiosResponse<Result<PaginatedList<UomType>>> = await this.axiosInstance.get(`uomtypes?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createUomType(request: UomType, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('uomtypes', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateUomType(uomTypeId: string, request: UomType, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`uomtypes/${uomTypeId}`, request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getUomType(uomTypeId: string, cancelToken?: CancelToken): Promise<Result<UomType>> {
    try {
      const response: AxiosResponse<Result<UomType>> = await this.axiosInstance.get(`uomtypes/${uomTypeId}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketCategory(categoryId: string, cancelToken?: CancelToken): Promise<Result<MarketCategory>> {
    try {
      const response: AxiosResponse<Result<MarketCategory>> = await this.axiosInstance.get(`marketcategories/${categoryId}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketCategories(options: MarketCategoryFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketCategory>>> {
    try {
      const params = new URLSearchParams({
        CompanyId: options.companyId ?? '',
        SearchTerm: options.searchTerm ?? '',
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();
      const response: AxiosResponse<Result<PaginatedList<MarketCategory>>> = await this.axiosInstance.get(`marketcategories?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createMarketCategory(request: MarketCategory, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketcategories', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateMarketCategory(categoryId: string, request: MarketCategory, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketcategories/${categoryId}`, request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketItem(itemId: string, companyId: string, customerTypeId: string, cancelToken?: CancelToken): Promise<Result<MarketItem>> {
    try {
      const response: AxiosResponse<Result<MarketItem>> = await this.axiosInstance.get(`marketitems/${itemId}/instance/${companyId}/market/${customerTypeId}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getMarketItems(options: MarketItemFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketItem>>> {
    try {
      const params = new URLSearchParams({
        CompanyId: options.companyId ?? '',
        CustomerTypeId: options.customerTypeId ?? '',
        SearchTerm: options.searchTerm ?? '',
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();
      const response: AxiosResponse<Result<PaginatedList<MarketItem>>> = await this.axiosInstance.get(`marketitems?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createMarketItem(request: MarketItem, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketitems', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateMarketItem(itemId: string, request: MarketItem, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketitems/${itemId}`, request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async downloadMarketItems(companyId: string, customerTypeId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const params = new URLSearchParams({
        CompanyId: companyId,
        CustomerTypeId: customerTypeId,
      }).toString();
      const response: AxiosResponse<Result<FileResponse>> = await this.axiosInstance.get(`marketitems/download?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async uploadMarketItems(request: MarketItemUploadRequest, cancelToken?: CancelToken): Promise<Result<MarketItemUploadResult>> {
    try {
      const response: AxiosResponse<Result<MarketItemUploadResult>> = await this.axiosInstance.post('marketitems/upload', request, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createOrUpdateAutoshipTemplate(request: AutoshipTemplate, storeNumber: string, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('autoshiptemplate', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getAutoshipTemplates(options: AutoshipmentTemplatesFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<PaginatedList<AutoshipTemplateModel>>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };

      const params = new URLSearchParams({
        Name: options.name ?? '',
        OrderTypeId: options.orderTypeId.toString(),
        StatusId: options.statusId.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();

      const response: AxiosResponse<Result<PaginatedList<AutoshipTemplateModel>>> = await this.axiosInstance.get(`autoshiptemplate?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getAutoshipTemplateById(id: string, storeNumber: string, includeExpandedDetails: boolean= false, cancelToken?: CancelToken): Promise<Result<AutoshipTemplateModel>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<AutoshipTemplateModel>> = await this.axiosInstance.get(`autoshiptemplate/${id}?includeExpandedDetails=${includeExpandedDetails}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async downloadItemsCsv(storeNumber: string, id: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<FileResponse>> = await this.axiosInstance.get(`downloaditemscsv/${id}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async uploadTemplateItemsCsv(storeNumber: string | undefined, orderTypeId: number, request: FileRequest, cancelToken?: CancelToken): Promise<Result<Item[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<Item[]>> = await this.axiosInstance.post(`uploadtemplateitemscsv/${orderTypeId}`, request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }


  public async createOrUpdateAutoshipSchedule(request: AutoshipSchedule, storeNumber: string, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('autoshipschedule', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getAutoshipSchedules(options: AutoshipmentSchedulesFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<PaginatedList<AutoshipScheduleModel>>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };

      const params = new URLSearchParams({
        Name: options.name ?? '',
        TemplateId: options.templateId?.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();

      const response: AxiosResponse<Result<PaginatedList<AutoshipScheduleModel>>> = await this.axiosInstance.get(`autoshipschedule?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getAutoshipScheduleById(id: number, storeNumber: string, cancelToken?: CancelToken): Promise<Result<AutoshipScheduleModel>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<AutoshipScheduleModel>> = await this.axiosInstance.get(`autoshipschedule/${id}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getDeliverySchedulesForStores(companyId: number, orderTypeId: number, stores: string, cancelToken?: CancelToken): Promise<Result<StoreAvailableODS[]>> {
    try {

      const params = new URLSearchParams({
        CompanyId: companyId.toString(),
        OrderTypeId: orderTypeId.toString(),
        Stores: stores,
      }).toString();

      const response: AxiosResponse<Result<StoreAvailableODS[]>> = await this.axiosInstance.get(`DeliverySchedulesForStores?${params}`, { cancelToken });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getAutoshipReasons(storeNumber: string, cancelToken?: CancelToken): Promise<Result<IdNameModel[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<IdNameModel[]>> = await this.axiosInstance.get('autoshipreasons', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async archiveTemplate(storeNumber: string, templateId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`archivetemplate/${templateId}`, null, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async deleteTemplate(storeNumber: string, templateId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(`deletetemplate/${templateId}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async deleteSchedule(storeNumber: string, scheduleId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(`deleteschedule/${scheduleId}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getOrderEdn(storeNumber?: string, generateNew?: boolean | undefined, orderId?: string | undefined, cancelToken?: CancelToken): Promise<RetailerEdnOrderModel> {
    try {
      const params = new URLSearchParams({
        generateNew: generateNew ? 'true' : 'false',
        orderId: orderId ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<RetailerEdnOrderModel>> = await this.axiosInstance.post(`fetchOrderEdnData?${params}`, null, { cancelToken, headers });
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getAutoshipTemplateNames(storeNumber: string, cancelToken?: CancelToken): Promise<Result<string[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };

      const response: AxiosResponse<Result<string[]>> = await this.axiosInstance.get('autoshiptemplatenames', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getDeliveryOrderItems(
    storeNumber: string,
    orderNumber: string,
    cancelToken?: CancelToken,
  ): Promise<Result<DeliveryOrderItemModel[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const params = { 
        OrderNumber: orderNumber,
      };

      const response: AxiosResponse<Result<DeliveryOrderItemModel[]>> =
        await this.axiosInstance.get('deliveryorderitems', { headers, params, cancelToken });
      return response.data;
    } catch (error) {
      console.error('Error fetching delivery order items:', error);
      throw error;
    }
  }

  public async updateDeliveryItems(
    storeNumber: string,
    items: DeliveryOrderItemUpdateModel[],
    cancelToken?: CancelToken,
  ): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> =
        await this.axiosInstance.post('updatedeliveryitems', items, { headers, cancelToken });
      return response.data;
    } catch (error) {
      console.error('Error updating delivery items:', error);
      throw error;
    }
  }

  public async markOrderAsReceived(
    storeNumber: string,
    orderNumber: string,
    cancelToken?: CancelToken,
  ): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const params = { 
        OrderNumber: orderNumber, 
      };
      const response: AxiosResponse<Result<boolean>> =
        await this.axiosInstance.post('markorderasreceived', null, { headers, params, cancelToken });
      return response.data;
    } catch (error) {
      console.error('Error marking order as received:', error);
      throw error;
    }
  }

  public async createCreditRequest(
    storeNumber: string,
    orderNumber: string,
    cancelToken?: CancelToken,
  ): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };

      const response: AxiosResponse<Result<boolean>> =
      await this.axiosInstance.post(`createCreditRequest/${orderNumber}`, null, { headers, cancelToken });

      return response.data;
    } catch (error) {
      console.error('Error creating credit request:', error);
      throw error;
    }
  }

  public async getCreditRequests (
    storeNumber: string,
    orderNumber: string,
    cancelToken?: CancelToken,
  ): Promise<Result<CreditRequestItemModel[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const params = { 
        OrderNumber: orderNumber,
      };

      const response: AxiosResponse<Result<CreditRequestItemModel[]>> =
        await this.axiosInstance.get('creditRequest', { headers, params, cancelToken });
      return response.data;
    } catch (error) {
      console.error('Error fetching credit request:', error);
      throw error;
    }
  }

  public async saveCreditRequest(
    storeNumber: string,
    items: SaveCreditRequestItemModel[],
    cancelToken?: CancelToken,
  ): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(
        'SaveCreditRequest',
        items,
        { headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      console.error('Error saving credit request:', error);
      throw error;
    }
  }
  
  public async downloadCreditRequestFiles(
    storeNumber: string,
    creditRequestId: number,
    cancelToken?: CancelToken,
  ): Promise<Blob> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Blob> = await this.axiosInstance.get(
        `DownloadCreditRequestFiles/${creditRequestId}`,
        { headers, responseType: 'blob', cancelToken },
      );
      return response.data;
    } catch (error) {
      console.error('Error downloading credit request files:', error);
      throw error;
    }
  }

  public async submitCreditRequest(
    storeNumber: string,
    orderNumber: string,
    cancelToken?: CancelToken,
  ): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };

      const response: AxiosResponse<Result<boolean>> =
      await this.axiosInstance.post(`submitCreditRequest/${orderNumber}`, null, { headers, cancelToken });

      return response.data;
    } catch (error) {
      console.error('Error submitting credit request:', error);
      throw error;
    }
  }

  public async getCreditValueForLine(
    storeNumber: string,
    request: CreditValueRequest,
    cancelToken?: CancelToken,
  ): Promise<Result<CreditValueResponse>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };

      const response: AxiosResponse<Result<CreditValueResponse>> =
      await this.axiosInstance.post(`getCreditValueForLine/${request.OrderNumber}`, request, { headers, cancelToken });

      return response.data;
    } catch (error) {
      console.error('Error getting credit value for line:', error);
      throw error;
    }
  }

  public async completeCreditReview(
    storeNumber: string, orderNumber: string, reviewLines: CreditReviewLineModel[],
    cancelToken?: CancelToken,
  ): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };

      const response: AxiosResponse<Result<boolean>> =
      await this.axiosInstance.post(`completeCreditReview?OrderNumber=${orderNumber}`, reviewLines, { headers, cancelToken });
      return response.data;
    } catch (error) {
      console.error('Error getting credit value for line:', error);
      throw error;
    }
  }

  public async getODS(options: DeliveryScheduleFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<PaginatedList<DeliveryScheduleModel>>> {
    try {
      const params = new URLSearchParams({
        InstanceId: options.instanceId?.toString(),
        MarketId: options.marketId?.toString(),
        DeliveryDateFrom: options.deliveryDateFrom?.toString(),
        DeliveryDateTo: options.deliveryDateTo?.toString(),
        CutOffDateFrom: options.cutOffDateFrom?.toString(),
        CutOffDateTo: options.cutOffDateTo?.toString(),
        OrderTypes: options.orderTypes?.toString(),
        SupplierNumber: options.supplierNumber?.toString(),
        Stores: options.stores?.toString(),
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<PaginatedList<DeliveryScheduleModel>>> = await this.axiosInstance.get(`ods?${params}`, { headers, cancelToken });
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async exportODS(options: DeliveryScheduleFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const params = new URLSearchParams({
        InstanceId: options.instanceId?.toString(),
        MarketId: options.marketId?.toString(),
        DeliveryDateFrom: options.deliveryDateFrom?.toString(),
        DeliveryDateTo: options.deliveryDateTo?.toString(),
        CutOffDateFrom: options.cutOffDateFrom?.toString(),
        CutOffDateTo: options.cutOffDateTo?.toString(),
        OrderTypes: options.orderTypes?.toString(),
        SupplierNumber: options.supplierNumber?.toString(),
        Stores: options.stores?.toString(),
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.get<Result<FileResponse>>(`ods/export?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async validateODS(request: FileRequest, storeNumber: string, instanceId: number, marketId: number, cancelToken?: CancelToken): Promise<Result<ODSImportResultModel>> {
    try {
      const params = new URLSearchParams({
        InstanceId: instanceId?.toString(),
        MarketId: marketId?.toString(),
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber ?? '' };

      const response: AxiosResponse<Result<ODSImportResultModel>> = await this.axiosInstance.post(`ods/validate?${params}`, request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async importODS(request: FileRequest, storeNumber: string, instanceId: number, marketId: number, languageCode: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const params = new URLSearchParams({
        InstanceId: instanceId?.toString(),
        MarketId: marketId?.toString(),
        LanguageCode: languageCode,
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`ods/import?${params}`, request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateBulkODSDate(request: BulkODSDateUpdateModel, storeNumber: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put('ods/bulk', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async deleteBulkODS(ids: number[], storeNumber: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const params = new URLSearchParams({
        Ids: ids.join(','),
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(`ods?${params}`, { 
        headers, 
        cancelToken,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async addODS(request: AddODSModel, storeNumber: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('ods/add', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getODSAvailability(
    options: { customerTypeId: number; orderTypeId?: number; supplierNumber?: string; startDate: string; endDate: string },
    cancelToken?: CancelToken,
  ): Promise<Result<Record<string, ODSAvailability>>> {
    try {
      const params = new URLSearchParams({
        customerTypeId: options.customerTypeId.toString(),
        startDate: options.startDate,
        endDate: options.endDate,
        ...(options.orderTypeId !== undefined && options.orderTypeId !== null && { orderTypeId: options.orderTypeId.toString() }),
        ...(options.supplierNumber && { supplierNumber: options.supplierNumber }),
      }).toString();
      const response: AxiosResponse<Result<Record<string, ODSAvailability>>> = await this.axiosInstance.get(`market/odsAvailability?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getFranchiseOrderTypes(customerTypeId: number, cancelToken?: CancelToken): Promise<Result<OrderType[]>> {
    try {
      const params = new URLSearchParams({
        customerTypeId: customerTypeId.toString(),
      }).toString();
      const response: AxiosResponse<Result<OrderType[]>> = await this.axiosInstance.get(`market/orderTypes?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getFranchiseCategories(customerTypeId: number, orderTypeId: number, cancelToken?: CancelToken): Promise<Result<Category[]>> {
    try {
      const params = new URLSearchParams({
        customerTypeId: customerTypeId.toString(),
        orderTypeId: orderTypeId.toString(),
      }).toString();
      const response: AxiosResponse<Result<Category[]>> = await this.axiosInstance.get(`market/categoriesForOrder?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getBulkOrderData(
    request: BulkOrdersRequest,
    cancelToken?: CancelToken,
  ): Promise<Result<BulkOrderItem[]>> {
    try {
      const response: AxiosResponse<Result<BulkOrderItem[]>> = await this.axiosInstance.post(
        'bulkOrders/data',
        {
          customerTypeId: request.customerTypeId,
          orderTypeId: request.orderTypeId,
          deliveryDate: request.deliveryDate,
          supplierNumber: request.supplierNumber || null,
          categoryId: request.categoryId || null,
          itemSearch: request.itemSearch || null,
        },
        { cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async downloadBulkOrders(request: BulkOrdersRequest, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const params = new URLSearchParams({
        customerTypeId: request.customerTypeId.toString(),
        orderTypeId: request.orderTypeId.toString(),
        deliveryDate: request.deliveryDate,
        supplierNumber: request.supplierNumber || '',
        categoryId: request.categoryId?.toString() || '',
        itemSearch: request.itemSearch || '',
      }).toString();
      const response = await this.axiosInstance.get<Result<FileResponse>>(
        `bulkOrders/download?${params}`,
        { cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async uploadBulkOrders(customerTypeId: number, orderTypeId: number, deliveryDate: string, request: FileRequest, supplierNumber?: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const rawParams: Record<string, string> = {
        customerTypeId: customerTypeId.toString(),
        orderTypeId: orderTypeId.toString(),
        deliveryDate: deliveryDate,
      };
      if (supplierNumber) rawParams.supplierNumber = supplierNumber;
      const params = new URLSearchParams(rawParams).toString();
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(
        `bulkOrders/upload?${params}`,
        request,
        { cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getDemandModifierById(storeNumber: string, id: number, cancelToken?: CancelToken): Promise<Result<DemandModifier>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<DemandModifier>> = await this.axiosInstance.get(
        `demandModifiers/${id}`,
        { headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getDemandModifiers(storeNumber: string, includeInactive = false, cancelToken?: CancelToken): Promise<Result<DemandModifier[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<DemandModifier[]>> = await this.axiosInstance.get(
        `demandModifiers?includeInactive=${includeInactive}`,
        { headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createDemandModifier(storeNumber: string, request: UpsertDemandModifierRequest, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post(
        'demandModifiers',
        request,
        { headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateDemandModifier(storeNumber: string, request: UpsertDemandModifierRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(
        'demandModifiers',
        request,
        { headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async deleteDemandModifier(storeNumber: string, id: number, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(
        `demandModifiers/${id}`,
        { headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getStoreLevelMOQs(
    options: StoreLevelMOQFilterOptions,
    cancelToken?: CancelToken,
  ): Promise<Result<PaginatedList<StoreLevelMOQ>>> {
    try {
      const params = new URLSearchParams({
        companyId: options.companyId ?? '',
        customerTypeId: options.customerTypeId ?? '',
        itemNumber: options.itemNumber ?? '',
        searchTerm: options.searchTerm ?? '',
        sort: options.sort ?? '',
        direction: options.direction ?? '',
        pageNumber: options.pageNumber.toString(),
        pageSize: options.pageSize.toString(),
      }).toString();
      console.log(params);
      const response: AxiosResponse<Result<PaginatedList<StoreLevelMOQ>>> =
        await this.axiosInstance.get(`storeLevelMOQs?${params}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateStoreLevelMOQ(
    moq: StoreLevelMOQ,
    cancelToken?: CancelToken,
  ): Promise<Result<number>> {
    try {
      const response: AxiosResponse<Result<number>> =
        await this.axiosInstance.post('storeLevelMOQs', moq, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }
}

export const client = Client.getInstance();
